import React, { useState, useEffect, useRef } from 'react';
import apiFetch from '../utils/apiFetch';
import { FiSearch, FiFilter, FiX, FiChevronDown, FiUser, FiDownload, FiBriefcase, FiUsers, FiCalendar, FiFileText, FiPercent, FiCheckCircle, FiInfo, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import '../../src/styles/style.css';
import '../styles/ExistingUser.css';
import CustomMarkdownRenderer from '../components/CustomMarkdownRenderer.js';
import HybridRendererSelector from '../components/HybridRendererSelector.js';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ExistingUserReAssessment = () => {
  // State management
  const [searchParams, setSearchParams] = useState({
    candidate: '',
    project: '',
    interviewer: '',
    keyword: ''
  });
  const [users, setUsers] = useState([]);
  const [fullDetails, setFullDetails] = useState(null);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'create_date', direction: 'desc' });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [projectsList, setProjectsList] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('results');
  const [useStructuredUI, setUseStructuredUI] = useState(true);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 10,
    total: 0
  });

  // Fetch projects list on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Function to fetch projects for dropdown
  const fetchProjects = async () => {
    try {
      const response = await apiFetch('/api/projects-dropdown');
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjectsList(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Function to fetch assessments with pagination
  const fetchAssessments = async (page = pagination.currentPage) => {
    setIsLoading(true);
    setError(null);

    try {
      // Construct the query parameters
      const queryParams = new URLSearchParams();

      // Add filters if they are provided
      if (searchParams.candidate) queryParams.append('candidate', searchParams.candidate);
      if (searchParams.project) queryParams.append('project', searchParams.project);
      if (searchParams.interviewer) queryParams.append('interviewer', searchParams.interviewer);
      if (searchParams.keyword) queryParams.append('keyword', searchParams.keyword);
      if (dateRange.from) queryParams.append('dateFrom', dateRange.from);
      if (dateRange.to) queryParams.append('dateTo', dateRange.to);
      
      // Add pagination parameters
      queryParams.append('page', page);
      queryParams.append('limit', pagination.limit);

      // Fetch data from the API
      const response = await apiFetch(`/api/assessment?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const { results, pagination: paginationInfo } = await response.json();

      // Normalize field names to lowercase for consistency
      const normalizedData = results.map(item => ({
        id: item.id,
        project: item.Project || item.project, // Handle both upper and lowercase field names
        candidate: item.Candidate || item.candidate,
        interviewer: item.Interviewer || item.interviewer,
        assessment_probability: item.assessment_probability,
        assessment_effectiveness: item.assessment_effectiveness,
        shortlisting_probability: item.shortlisting_probability,
        interview_effectiveness: item.interview_effectiveness,
        intext_data: item.intext_data,
        create_date: item.create_date,
        update_date: item.update_date || item.updated_date // Handle different date field name
      }));

      setUsers(normalizedData);
      setPagination({
        currentPage: paginationInfo.page,
        totalPages: paginationInfo.totalPages,
        limit: paginationInfo.limit,
        total: paginationInfo.total
      });
      setTotalResults(paginationInfo.total);
      setIsResultsVisible(normalizedData.length > 0);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setError(error.message || 'Failed to fetch assessment data');
      setUsers([]);
      setIsResultsVisible(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle page changes
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchAssessments(newPage);
  };

  // Function to handle limit changes
  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      currentPage: 1 // Reset to first page when changing limit
    }));
    fetchAssessments(1); // Fetch first page with new limit
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setSearchParams({
      ...searchParams,
      [field]: value
    });
  };

  // Handle project selection
  const handleProjectSelect = (project) => {
  setSearchParams(prev => ({
    ...prev,
    project
  }));
  // Only close dropdown when actually selecting a project, not during search
  setShowProjectDropdown(false);
  setProjectSearchTerm(''); // Reset search term after selection
};

  // Reset all filters
  const handleReset = () => {
    setSearchParams({
      candidate: '',
      project: '',
      interviewer: '',
      keyword: ''
    });
    setDateRange({ from: '', to: '' });
    setUsers([]);
    setFullDetails(null);
    setIsResultsVisible(false);
    setActiveFilter('all');
    setProjectSearchTerm('');
  };

  // Handle search button click
  const handleSearch = () => {
    fetchAssessments();
  };

  // Handle view details
  const handleDetailsClick = (user) => {
    setFullDetails(user);
    setSelectedTab('results'); // Default to results tab when viewing details
  };

  // Handle tab switching
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  // Toggle UI mode between structured and markdown
  const toggleUIMode = () => {
    setUseStructuredUI(!useStructuredUI);
  };

  // Handle table sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter projects based on search term
  const filteredProjects = projectSearchTerm
    ? projectsList.filter(project =>
      project.toLowerCase().includes(projectSearchTerm.toLowerCase()))
    : projectsList;

  // Get sorted data
  const getSortedData = () => {
    const sortableData = [...users];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // Handle numeric values
        if (typeof a[sortConfig.key] === 'number') {
          return sortConfig.direction === 'asc'
            ? a[sortConfig.key] - b[sortConfig.key]
            : b[sortConfig.key] - a[sortConfig.key];
        }

        // Handle dates
        if (sortConfig.key === 'create_date' || sortConfig.key === 'update_date') {
          const dateA = new Date(a[sortConfig.key]);
          const dateB = new Date(b[sortConfig.key]);
          return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // Handle strings
        if (!a[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (!b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;

        if (a[sortConfig.key].toLowerCase() < b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key].toLowerCase() > b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Apply filters based on active filter
  const getFilteredData = () => {
    const sortedData = getSortedData();

    if (activeFilter === 'all') {
      return sortedData;
    }

    if (activeFilter === 'highScore') {
      return sortedData.filter(user => {
        const score = parseFloat(user.shortlisting_probability) || 0;
        return score >= 75;
      });
    }

    if (activeFilter === 'mediumScore') {
      return sortedData.filter(user => {
        const score = parseFloat(user.shortlisting_probability) || 0;
        return score >= 50 && score < 75;
      });
    }

    if (activeFilter === 'lowScore') {
      return sortedData.filter(user => {
        const score = parseFloat(user.shortlisting_probability) || 0;
        return score < 50;
      });
    }

    return sortedData;
  };

  // Get score class for styling
  const getScoreClass = (score) => {
    const numScore = parseFloat(score) || 0;
    if (numScore >= 75) return 'high-score';
    if (numScore >= 50) return 'medium-score';
    return 'low-score';
  };

  const filteredData = getFilteredData();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProjectDropdown && !event.target.closest('.project-dropdown-container')) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProjectDropdown]);

  /**
 * A helper function to wait for all images within an element to load.
 * This is more reliable than a fixed setTimeout.
 * @param {HTMLElement} element - The parent element to search for images.
 * @returns {Promise<void>} A promise that resolves when all images have loaded.
 */
const waitForImages = (element) => {
  const images = Array.from(element.querySelectorAll('img'));
  const promises = images.map(img => {
    return new Promise((resolve) => {
      if (img.complete) {
        resolve();
      } else {
        img.onload = resolve;
        img.onerror = resolve; // Resolve on error too, so we don't wait forever
      }
    });
  });
  return Promise.all(promises);
};

const exportToPDF = async () => {
  const button = document.querySelector('.icon-export-button');
  if (!button) {
    console.error('Export button not found.');
    return;
  }
  const prevButtonText = button.innerHTML;
  button.innerHTML = '<span class="export-loading"></span>';

  // Store original body/html styles to restore them later
  const originalBodyStyle = document.body.style.cssText;
  const originalHtmlStyle = document.documentElement.style.cssText;

  let clone; // Define clone here to access it in the finally block

  try {
    const modal = document.querySelector('.details-modal');
    if (!modal) {
      alert('Details modal not found.');
      throw new Error('Details modal not found.');
    }
    clone = modal.cloneNode(true);

    // --- FIX 1: More Robust Styling for Full Content Rendering ---

    // Apply styles to the document itself to prevent clipping the off-screen clone
    Object.assign(document.documentElement.style, {
      overflow: 'visible',
      height: 'auto',
    });
    Object.assign(document.body.style, {
      overflow: 'visible',
      height: 'auto',
    });
    
    // Apply styles to the clone to make it a fixed-width, full-height block
    Object.assign(clone.style, {
      position: 'absolute',
      top: '0',
      left: '-9999px', // Position at top-left for consistent capturing
      width: '800px', // A standard width for A4 printing
      padding: '20px',
      backgroundColor: 'white',
      zIndex: '-1', // Bring to front to ensure it's rendered
      height: 'auto', // Allow height to expand
      maxHeight: 'none',
      overflow: 'visible',
    });

    // Remove the close button from the clone
    const closeBtn = clone.querySelector('.modal-close');
    if (closeBtn) closeBtn.remove();

    // Force all tab contents to be visible for capture
    clone.querySelectorAll('.tab-content').forEach((tab) => {
      tab.style.display = 'block';
    });

    // Expand any internal scrollable elements
    clone.querySelectorAll('[style*="overflow"]').forEach((el) => {
      el.style.overflow = 'visible';
      el.style.height = 'auto';
      el.style.maxHeight = 'none';
    });
    
    document.body.appendChild(clone);

    // Wait for images to load AND for the browser to reflow
    await waitForImages(clone);
    await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay for final render

    // --- FIX 2: Improved html2canvas Options ---
    const canvas = await html2canvas(clone, {
      scale: 2, // Higher resolution
      useCORS: true,
      logging: false,
      // Use the clone's full scroll dimensions, not just its visible size
      width: clone.scrollWidth,
      height: clone.scrollHeight,
      // Scroll to the top of the page for a consistent capture viewport
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0
    });

    const title = `${fullDetails.candidate || 'Candidate'} - ${fullDetails.project || 'Assessment'} (${formatDate(fullDetails.create_date)})`;
    const imgData = canvas.toDataURL('image/png');

    // --- FIX 3: Correct Multi-Page PDF Generation Logic ---
    const pdf = new jsPDF('p', 'mm', 'a4'); // A4 size: 210mm x 297mm
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const canvasAspectRatio = canvasHeight / canvasWidth;

    const imgWidth = pdfWidth; // Fit image to the full width of the PDF page
    const imgHeight = imgWidth * canvasAspectRatio;

    let heightLeft = imgHeight;
    let position = 0;
    const topMargin = 15; // Margin for the title

    // Add title
    pdf.setFontSize(16);
    pdf.text(title, 10, 10);

    // Add the first page
    pdf.addImage(imgData, 'PNG', 0, topMargin, imgWidth, imgHeight);
    heightLeft -= (pdfHeight - topMargin);

    // Add new pages as long as there is content left
    while (heightLeft > 0) {
      position -= pdfHeight; // Move the position "up" by one page height
      pdf.addPage();
      // Add the same image, but use the new 'position' to crop it from the top
      pdf.addImage(imgData, 'PNG', 0, position + topMargin, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`${title.replace(/\s+/g, '_')}.pdf`);

  } catch (err) {
    console.error('PDF generation error:', err);
    alert('There was an error generating the PDF. Please check the console for details.');
  } finally {
    // --- FIX 4: Robust Cleanup ---
    // Restore original styles
    document.body.style.cssText = originalBodyStyle;
    document.documentElement.style.cssText = originalHtmlStyle;

    // Remove the clone from the DOM
    if (clone) {
      clone.remove();
    }
    // Restore the button's original state
    button.innerHTML = prevButtonText;
  }
};




  const exportToPDFEnhanced = async () => {
  const buttonEl = document.querySelector('.icon-export-button');
  const prevButtonText = buttonEl.innerHTML;
  buttonEl.innerHTML = '<span class="export-loading"></span>';
  
  try {
    // Get clean data
    const candidateName = fullDetails.candidate || 'Candidate';
    const projectName = fullDetails.project || 'Project';
    const interviewerName = fullDetails.interviewer || 'Interviewer';
    const assessmentDate = formatDate(fullDetails.create_date);
    const matchScore = fullDetails.shortlisting_probability || 'N/A';
    const effectivenessScore = fullDetails.interview_effectiveness || 'N/A';
    
    // Create PDF document
    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPos = 20;

    // Add header
    pdf.setFillColor(52, 152, 219);
    pdf.rect(0, 0, 210, 15, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.text('ASSESSMENT REPORT', 15, 10);
    pdf.setTextColor(0, 0, 0);
    
    // Add title
    pdf.setFontSize(18);
    pdf.setFont(undefined, 'bold');
    pdf.text(`Assessment for ${candidateName}`, 15, yPos);
    yPos += 10;

    // Add metadata
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'normal');
    pdf.text(`Project: ${projectName}`, 15, yPos);
    yPos += 6;
    pdf.text(`Interviewer: ${interviewerName}`, 15, yPos);
    yPos += 6;
    pdf.text(`Date: ${assessmentDate}`, 15, yPos);
    yPos += 6;
    
    // Add scores in colored boxes
    yPos += 5;
    pdf.setDrawColor(220, 220, 220);
    
    // Score box 1
    let scoreColor = getScoreClass(matchScore);
    if (scoreColor === 'high-score') {
      pdf.setFillColor(46, 125, 50); // Green
    } else if (scoreColor === 'medium-score') {
      pdf.setFillColor(255, 143, 0); // Orange
    } else {
      pdf.setFillColor(198, 40, 40); // Red
    }
    
    pdf.roundedRect(15, yPos, 80, 12, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.text(`Match Score: ${matchScore}%`, 20, yPos + 8);
    
    // Score box 2
    pdf.setFillColor(69, 90, 100); // Blue-gray
    pdf.roundedRect(115, yPos, 80, 12, 2, 2, 'F');
    pdf.text(`Effectiveness: ${effectivenessScore}%`, 120, yPos + 8);
    
    yPos += 20;
    pdf.setTextColor(0, 0, 0);
    
    // Get assessment data
    try {
      const assessmentData = JSON.parse(fullDetails.assessment_probability);
      const effectivenessData = JSON.parse(fullDetails.assessment_effectiveness);
      
      // Add assessment section
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Assessment Results', 15, yPos);
      yPos += 8;
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(11);
      
      // Add structured assessment content
      if (assessmentData && assessmentData.structured) {
        const structuredData = assessmentData.structured;
        
        // Technical Skills
        if (structuredData.technicalSkills && structuredData.technicalSkills.length > 0) {
          pdf.setFont(undefined, 'bold');
          pdf.text('Technical Skills:', 15, yPos);
          yPos += 6;
          pdf.setFont(undefined, 'normal');
          
          structuredData.technicalSkills.forEach(skill => {
            if (yPos > 270) {
              pdf.addPage();
              yPos = 20;
            }
            pdf.text(`• ${skill.skill}: ${skill.rating}/5 - ${skill.evidence}`, 20, yPos);
            yPos += 6;
          });
          
          yPos += 4;
        }
        
        // Soft Skills
        if (structuredData.softSkills && structuredData.softSkills.length > 0) {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          
          pdf.setFont(undefined, 'bold');
          pdf.text('Soft Skills:', 15, yPos);
          yPos += 6;
          pdf.setFont(undefined, 'normal');
          
          structuredData.softSkills.forEach(skill => {
            pdf.text(`• ${skill.skill}: ${skill.rating}/5 - ${skill.evidence}`, 20, yPos);
            yPos += 6;
          });
          
          yPos += 4;
        }
        
        // Strengths and Development Areas
        if (structuredData.keyStrengths || structuredData.developmentAreas) {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          
          if (structuredData.keyStrengths) {
            pdf.setFont(undefined, 'bold');
            pdf.text('Key Strengths:', 15, yPos);
            yPos += 6;
            pdf.setFont(undefined, 'normal');
            
            structuredData.keyStrengths.forEach(strength => {
              pdf.text(`• ${strength}`, 20, yPos);
              yPos += 5;
            });
            
            yPos += 4;
          }
          
          if (structuredData.developmentAreas) {
            if (yPos > 270) {
              pdf.addPage();
              yPos = 20;
            }
            
            pdf.setFont(undefined, 'bold');
            pdf.text('Development Areas:', 15, yPos);
            yPos += 6;
            pdf.setFont(undefined, 'normal');
            
            structuredData.developmentAreas.forEach(area => {
              pdf.text(`• ${area}`, 20, yPos);
              yPos += 5;
            });
            
            yPos += 4;
          }
        }
        
        // Overall Recommendation
        if (structuredData.overallRecommendation) {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          
          pdf.setFont(undefined, 'bold');
          pdf.text('Overall Recommendation:', 15, yPos);
          yPos += 6;
          pdf.setFont(undefined, 'normal');
          
          const lines = pdf.splitTextToSize(structuredData.overallRecommendation, 180);
          pdf.text(lines, 15, yPos);
          yPos += lines.length * 6 + 4;
        }
      }
      
      // Add effectiveness section if we're viewing it
      if (selectedTab === 'effectiveness' && effectivenessData && effectivenessData.structured) {
        if (yPos > 250) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('Interview Effectiveness', 15, yPos);
        yPos += 8;
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(11);
        
        // Coverage
        const struct = effectivenessData.structured;
        
        if (struct.keySkills && struct.keySkills.length > 0) {
          pdf.setFont(undefined, 'bold');
          pdf.text('Skills Coverage:', 15, yPos);
          yPos += 6;
          pdf.setFont(undefined, 'normal');
          
          struct.keySkills.forEach(skill => {
            if (yPos > 270) {
              pdf.addPage();
              yPos = 20;
            }
            const assessedText = skill.assessed ? "Yes" : "No";
            pdf.text(`• ${skill.skill}: ${assessedText} (Depth: ${skill.depthOfInquiry}/5)`, 20, yPos);
            yPos += 5;
          });
          
          yPos += 4;
        }
        
        // Coverage gaps
        if (struct.coverageGaps && struct.coverageGaps.length > 0) {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          
          pdf.setFont(undefined, 'bold');
          pdf.text('Coverage Gaps:', 15, yPos);
          yPos += 6;
          pdf.setFont(undefined, 'normal');
          
          struct.coverageGaps.forEach(gap => {
            pdf.text(`• ${gap}`, 20, yPos);
            yPos += 5;
          });
          
          yPos += 4;
        }
        
        // Effectiveness metrics
        if (struct.effectivenessMetrics) {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          
          pdf.setFont(undefined, 'bold');
          pdf.text('Effectiveness Metrics:', 15, yPos);
          yPos += 6;
          pdf.setFont(undefined, 'normal');
          
          const metrics = struct.effectivenessMetrics;
          pdf.text(`• Skills Coverage: ${metrics.skillsCoverageScore}%`, 20, yPos);
          yPos += 5;
          pdf.text(`• Overall Effectiveness: ${metrics.overallEffectiveness}%`, 20, yPos);
          yPos += 5;
        }
      }
      
    } catch (parseError) {
      console.error("Error parsing JSON data:", parseError);
      pdf.text("Error parsing assessment data. Using fallback method.", 15, yPos);
      yPos += 10;
      
      // Fallback to just including the markdown text
      const textContent = fullDetails.assessment_probability;
      if (textContent) {
        const chunks = chunkText(textContent, 90);
        chunks.forEach(chunk => {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          pdf.text(chunk, 15, yPos);
          yPos += 5;
        });
      }
    }
    
    // Add additional notes if available
    if (fullDetails.intext_data) {
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Additional Notes', 15, yPos);
      yPos += 8;
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);
      
      const notes = fullDetails.intext_data;
      const lines = pdf.splitTextToSize(notes, 180);
      pdf.text(lines, 15, yPos);
    }
    
    // Add footer
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`, 15, 285);
    }
    
    // Save the PDF
    pdf.save(`${candidateName}_Assessment_${assessmentDate.replace(/\s+/g, '_')}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('There was an error generating the PDF. Please try again.');
  } finally {
    buttonEl.innerHTML = prevButtonText;
  }
};

// Helper function to chunk text for PDF
const chunkText = (text, lineLength) => {
  const chunks = [];
  let i = 0;
  while (i < text.length) {
    chunks.push(text.slice(i, i + lineLength));
    i += lineLength;
  }
  return chunks;
};

  const PaginationControls = () => {
  if (pagination.totalPages <= 1) return null;
  
  // Calculate which results are being shown
  const firstResult = (pagination.currentPage - 1) * pagination.limit + 1;
  const lastResult = Math.min(pagination.currentPage * pagination.limit, pagination.total);
  
  return (
    <div className="pagination-container">
      <div className="pagination-info">
        {firstResult}-{lastResult} of {pagination.total} Results
      </div>
      
      <div className="pagination-controls">
        {/* First page button */}
        <button 
          className="pagination-btn first"
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(1)}
          title="First page"
        >
          &laquo;
        </button>
        
        {/* Previous button */}
        <button 
          className="pagination-btn prev"
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          title="Previous page"
        >
          &lt;
        </button>
        
        {/* Page buttons */}
        {Array.from({ length: pagination.totalPages }, (_, i) => (
          <button
            key={i + 1}
            className={`pagination-btn page ${pagination.currentPage === i + 1 ? 'active' : ''}`}
            onClick={() => handlePageChange(i + 1)}
          >
            {i + 1}
          </button>
        ))}
        
        {/* Next button */}
        <button 
          className="pagination-btn next"
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          title="Next page"
        >
          &gt;
        </button>
        
        {/* Last page button */}
        <button 
          className="pagination-btn last"
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
          title="Last page"
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

  return (
    <div className="existing-user-container">
      {/* <div className="page-header">
        <h1>Assessment Records</h1>
        <p className="subtitle">Search and view past assessment results</p>
      </div> */}

      {/* Search Panel */}
      <div className="search-panel">
        <div className="search-fields">
          <div className="search-field">
            <div className="field-icon">
              <FiUser />
            </div>
            <input
              type="text"
              placeholder="Candidate Name"
              value={searchParams.candidate}
              onChange={(e) => handleInputChange('candidate', e.target.value)}
              className="search-input"
            />
          </div>

          {/* Project Dropdown */}
          <div className="search-field project-dropdown-container">
            <div className="field-icon">
              <FiBriefcase />
            </div>
            <div
              className={`dropdown-field ${showProjectDropdown ? 'active' : ''}`}
              onClick={() => setShowProjectDropdown(!showProjectDropdown)}
            >
              <input
                type="text"
                placeholder="Select Project"
                value={searchParams.project}
                readOnly
                className="search-input dropdown-input"
              />
              <FiChevronDown className={`dropdown-arrow ${showProjectDropdown ? 'up' : ''}`} />
            </div>

            {showProjectDropdown && (
  <div className="projects-dropdown">
    <div className="dropdown-search">
      <input
        type="text"
        placeholder="Search projects..."
        value={projectSearchTerm}
        onChange={(e) => setProjectSearchTerm(e.target.value)}
        // Add these event handlers to stop propagation
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="dropdown-search-input"
        // Add this to automatically focus the search input when dropdown opens
        autoFocus
      />
    </div>
    <div 
      className="dropdown-options"
      // Add this to prevent closing when clicking options area
      onClick={(e) => e.stopPropagation()} 
    >
      <div
        className={`dropdown-option ${searchParams.project === '' ? 'selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          handleProjectSelect('');
        }}
      >
        All Projects
      </div>
      {filteredProjects.map((project, index) => (
        <div
          key={index}
          className={`dropdown-option ${searchParams.project === project ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleProjectSelect(project);
          }}
        >
          {project}
        </div>
      ))}
      {filteredProjects.length === 0 && (
        <div className="no-options">No matching projects</div>
      )}
    </div>
  </div>
)}
          </div>

          <div className="search-field">
            <div className="field-icon">
              <FiUsers />
            </div>
            <input
              type="text"
              placeholder="Interviewer Name"
              value={searchParams.interviewer}
              onChange={(e) => handleInputChange('interviewer', e.target.value)}
              className="search-input"
            />
          </div>

          <div className="search-actions">
            {/* <button
              className="filter-button"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
            >
              <FiFilter />
              <span>Filters</span>
            </button> */}
            <button
              onClick={handleSearch}
              className="search-button"
              disabled={isLoading}
            >
              {isLoading ? 'Searching...' : 'Search'}
            </button>
            <button onClick={handleReset} className="reset-button">Reset</button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilterPanel && (
          <div className="advanced-filters">
            <div className="filter-section">
              <h4>Date Range</h4>
              <div className="date-range">
                <div className="date-field">
                  <label>From:</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  />
                </div>
                <div className="date-field">
                  <label>To:</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="filter-section">
              <h4>Keyword Search</h4>
              <div className="keyword-field">
                <input
                  type="text"
                  placeholder="Search in assessment content"
                  value={searchParams.keyword}
                  onChange={(e) => handleInputChange('keyword', e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <FiInfo /> {error}
        </div>
      )}

      {/* Results Section */}
      {isLoading ? (
        <div className="loading-section">
          <div className="loading-spinner"></div>
          <p>Searching assessment records...</p>
        </div>
      ) : isResultsVisible && (
        <div className="assessment-results">
          <div className="results-header">
            <h2>Assessment Results</h2>
            <div className="results-meta">
              <span className="results-count">{totalResults} records found</span>
              <div className="filter-tabs">
                <button
                  className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All
                </button>
                <button
                  className={`filter-tab ${activeFilter === 'highScore' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('highScore')}
                >
                  High Score
                </button>
                <button
                  className={`filter-tab ${activeFilter === 'mediumScore' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('mediumScore')}
                >
                  Medium Score
                </button>
                <button
                  className={`filter-tab ${activeFilter === 'lowScore' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('lowScore')}
                >
                  Low Score
                </button>
              </div>
            </div>
          </div>

          <div className="table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('project')}>
                    Project
                    {sortConfig.key === 'project' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort('candidate')}>
                    Candidate
                    {sortConfig.key === 'candidate' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort('interviewer')}>
                    Interviewer
                    {sortConfig.key === 'interviewer' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort('shortlisting_probability')}>
                    Match Score
                    {sortConfig.key === 'shortlisting_probability' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort('interview_effectiveness')}>
                    Effectiveness
                    {sortConfig.key === 'interview_effectiveness' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort('create_date')}>
                    Date
                    {sortConfig.key === 'create_date' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(user => (
                  <tr key={user.id}>
                    <td className="project-cell">{user.project}</td>
                    <td className="candidate-cell">{user.candidate}</td>
                    <td className="interviewer-cell">{user.interviewer}</td>
                    <td className="score-cell">
                      <div className={`score-badge ${getScoreClass(user.shortlisting_probability)}`}>
                        <FiPercent size={14} />
                        <span>{user.shortlisting_probability}%</span>
                      </div>
                    </td>
                    <td className="effectiveness-cell">
                      {user.interview_effectiveness ? (
                        <div className="effectiveness-indicator">
                          <FiCheckCircle size={16} />
                          <span>{user.interview_effectiveness}%</span>
                        </div>
                      ) : 'N/A'}
                    </td>
                    <td className="date-cell">{formatDate(user.create_date)}</td>
                    <td className="actions-cell">
                      <button
                        className="view-button"
                        onClick={() => handleDetailsClick(user)}
                      >
                        <FiFileText size={16} />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isResultsVisible && <PaginationControls />}
        </div>
      )}

      {/* Empty Results Message */}
      {!isLoading && isResultsVisible && filteredData.length === 0 && (
        <div className="empty-results">
          <div className="empty-icon">
            <FiFilter size={48} />
          </div>
          <h3>No matching records found</h3>
          <p>Try adjusting your filters or search criteria</p>
        </div>
      )}

      {/* Assessment Details Modal */}
      {fullDetails && (
        <div className="details-modal-overlay">
          <div className="details-modal">
            {/* Simplified header with just close button */}
            <button className="close-button modal-close" onClick={() => setFullDetails(null)}>
              <FiX size={20} />
            </button>

            {/* Compact metadata bar */}
            <div className="details-compact-meta">
              <div className="meta-item-group">
                <div className="meta-primary">
                  <FiBriefcase size={16} />
                  <span className="meta-value">{fullDetails.project || 'No Project'}</span>
                </div>
                <div className="meta-primary">
                  <FiUser size={16} />
                  <span className="meta-value">{fullDetails.candidate || 'No Candidate'}</span>
                </div>
                <div className="meta-primary">
                  <FiUsers size={16} />
                  <span className="meta-value">{fullDetails.interviewer || 'No Interviewer'}</span>
                </div>
                <div className={`score-pill ${getScoreClass(fullDetails.shortlisting_probability)}`} title="Shortlisting Probability">
                  <FiPercent size={14} />
                  <span>{fullDetails.shortlisting_probability}%</span>
                </div>
                <div className={`score-pill ${getScoreClass(fullDetails.interview_effectiveness)}`} title="Interview Effectiveness">
                  <FiPercent size={14} />
                  <span>{fullDetails.interview_effectiveness}%</span>
                </div>
              </div>

              <div className="meta-item-group">
                <div className="meta-secondary">
                  <FiCalendar size={14} />
                  <span>{formatDate(fullDetails.create_date)}</span>
                </div>
                {/* <div className={`score-pill ${getScoreClass(fullDetails.shortlisting_probability)}`}>
            <FiPercent size={14} />
            <span>{fullDetails.shortlisting_probability}%</span>
          </div> */}
              </div>
            </div>

            {/* Tab navigation */}
            <div className="details-tabs">
              <button
                className={`tab-button ${selectedTab === 'results' ? 'active' : ''}`}
                onClick={() => handleTabChange('results')}
              >
                Assessment Results
              </button>
              <button
                className={`tab-button ${selectedTab === 'effectiveness' ? 'active' : ''}`}
                onClick={() => handleTabChange('effectiveness')}
              >
                Interview Effectiveness
              </button>
              <div className="ui-toggle">
                {/* <label className="toggle-label">
            <input 
              type="checkbox" 
              checked={useStructuredUI} 
              onChange={toggleUIMode} 
            />
            <span className="toggle-text">Structured</span>
          </label> */}

                {/* Export button as icon */}
                <button
                  className="icon-export-button"
                  title="Export to PDF"
                  onClick={exportToPDF}
                  /* onClick={exportToPDFEnhanced} */
                >
                  <FiDownload size={18} />
                </button>
              </div>
            </div>

            {/* Content area */}
            <div className="details-content">
              <div className="tab-content">
                {selectedTab === 'results' ? (
                  <HybridRendererSelector
                    assessmentData={fullDetails.assessment_probability}
                    type="skills"
                    useStructured={useStructuredUI}
                  />
                ) : (
                  <HybridRendererSelector
                    assessmentData={fullDetails.assessment_effectiveness}
                    type="effectiveness"
                    useStructured={useStructuredUI}
                  />
                )}
              </div>

              {fullDetails.intext_data && (
                <div className="additional-notes">
                  <h3>Additional Notes</h3>
                  <div className="notes-content">
                    {fullDetails.intext_data}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExistingUserReAssessment;